import { useState, useContext } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Stack,
  Chip,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Divider,
  Grid,
} from '@mui/material';
import axiosInstance from '../../lib/axios';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import PaymentIcon from '@mui/icons-material/Payment';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import StorefrontIcon from '@mui/icons-material/Storefront';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import InventoryOutlinedIcon from '@mui/icons-material/InventoryOutlined';
import HistoryIcon from '@mui/icons-material/History';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import VerifiedIcon from '@mui/icons-material/Verified';
import BusinessIcon from '@mui/icons-material/Business';
import UndoIcon from '@mui/icons-material/Undo';
import { useOrder } from '../../hooks/useOrder';
import {
  useUpdatePaymentStatus,
  useUpdateFulfillmentStatus,
  useCancelOrder,
} from '../../hooks/useOrderMutations';
import {
  PaymentSummaryCard,
  FulfillmentStatusChip,
  OrderTypeBadge,
  OrderHistoryTimeline,
  OrderStockValidationPanel,
  OrderRefundModal,
} from '../../components/orders';
import type { OrderStockCheck, TransferRequest } from '../../components/orders';
import { palette, theme as customTheme, typography } from '../../theme/colors';
import { ColorModeContext } from '../../contexts/ColorModeContext';
import { useAnimationStyles } from '../../hooks/useAnimations';
import { useCanViewFinancials } from '../../hooks/usePermissions';
import {
  OrderPaymentStatus,
  OrderFulfillmentStatus,
  OrderType,
  FulfillmentMethod,
} from '../../types';

// Allowed payment status transitions
const allowedPaymentTransitions: Record<OrderPaymentStatus, OrderPaymentStatus[]> = {
  [OrderPaymentStatus.UNPAID]: [OrderPaymentStatus.PARTIALLY_PAID, OrderPaymentStatus.PAID],
  [OrderPaymentStatus.PARTIALLY_PAID]: [OrderPaymentStatus.PAID, OrderPaymentStatus.REFUNDED_PARTIAL],
  [OrderPaymentStatus.PAID]: [OrderPaymentStatus.REFUNDED_PARTIAL, OrderPaymentStatus.REFUNDED_FULL],
  [OrderPaymentStatus.REFUNDED_PARTIAL]: [OrderPaymentStatus.REFUNDED_FULL],
  [OrderPaymentStatus.REFUNDED_FULL]: [],
};

// Allowed fulfillment status transitions
const allowedFulfillmentTransitions: Record<OrderFulfillmentStatus, OrderFulfillmentStatus[]> = {
  [OrderFulfillmentStatus.PENDING]: [OrderFulfillmentStatus.CONFIRMED, OrderFulfillmentStatus.CANCELLED],
  [OrderFulfillmentStatus.WAITING_TRANSFER]: [OrderFulfillmentStatus.CANCELLED],
  [OrderFulfillmentStatus.TRANSFER_ISSUE]: [OrderFulfillmentStatus.WAITING_TRANSFER, OrderFulfillmentStatus.CANCELLED],
  [OrderFulfillmentStatus.WAITING_ADMIN_ACTION]: [OrderFulfillmentStatus.CONFIRMED, OrderFulfillmentStatus.CANCELLED],
  [OrderFulfillmentStatus.CONFIRMED]: [OrderFulfillmentStatus.READY_FOR_PICKUP, OrderFulfillmentStatus.READY_FOR_DELIVERY, OrderFulfillmentStatus.CANCELLED],
  [OrderFulfillmentStatus.READY_FOR_PICKUP]: [OrderFulfillmentStatus.PICKED_UP, OrderFulfillmentStatus.COMPLETED, OrderFulfillmentStatus.CANCELLED],
  [OrderFulfillmentStatus.READY_FOR_DELIVERY]: [OrderFulfillmentStatus.OUT_FOR_DELIVERY, OrderFulfillmentStatus.CANCELLED],
  [OrderFulfillmentStatus.OUT_FOR_DELIVERY]: [OrderFulfillmentStatus.DELIVERED, OrderFulfillmentStatus.COMPLETED, OrderFulfillmentStatus.CANCELLED],
  [OrderFulfillmentStatus.DELIVERED]: [OrderFulfillmentStatus.COMPLETED],
  [OrderFulfillmentStatus.PICKED_UP]: [OrderFulfillmentStatus.COMPLETED],
  [OrderFulfillmentStatus.COMPLETED]: [],
  [OrderFulfillmentStatus.CANCELLED]: [],
  [OrderFulfillmentStatus.EXPIRED]: [],
};

// Payment status labels for display
const paymentStatusLabels: Record<OrderPaymentStatus, string> = {
  [OrderPaymentStatus.UNPAID]: 'Non payé',
  [OrderPaymentStatus.PARTIALLY_PAID]: 'Partiellement payé',
  [OrderPaymentStatus.PAID]: 'Payé',
  [OrderPaymentStatus.REFUNDED_PARTIAL]: 'Remboursé partiellement',
  [OrderPaymentStatus.REFUNDED_FULL]: 'Remboursé',
};

// Fulfillment status labels for display
const fulfillmentStatusLabels: Record<OrderFulfillmentStatus, string> = {
  [OrderFulfillmentStatus.PENDING]: 'En attente',
  [OrderFulfillmentStatus.WAITING_TRANSFER]: 'Attente transfert',
  [OrderFulfillmentStatus.TRANSFER_ISSUE]: 'Problème transfert',
  [OrderFulfillmentStatus.WAITING_ADMIN_ACTION]: 'Attente admin',
  [OrderFulfillmentStatus.CONFIRMED]: 'Confirmé',
  [OrderFulfillmentStatus.READY_FOR_PICKUP]: 'Prêt à retirer',
  [OrderFulfillmentStatus.READY_FOR_DELIVERY]: 'Prêt à livrer',
  [OrderFulfillmentStatus.OUT_FOR_DELIVERY]: 'En livraison',
  [OrderFulfillmentStatus.DELIVERED]: 'Livré',
  [OrderFulfillmentStatus.PICKED_UP]: 'Retiré',
  [OrderFulfillmentStatus.COMPLETED]: 'Terminé',
  [OrderFulfillmentStatus.CANCELLED]: 'Annulé',
  [OrderFulfillmentStatus.EXPIRED]: 'Expiré',
};

export const OrderShow = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { mode } = useContext(ColorModeContext);
  const animations = useAnimationStyles();
  const canViewCosts = useCanViewFinancials();

  // Dialog states
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [fulfillmentDialogOpen, setFulfillmentDialogOpen] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [refundDialogOpen, setRefundDialogOpen] = useState(false);
  const [pendingPaymentStatus, setPendingPaymentStatus] = useState<OrderPaymentStatus | ''>('');
  const [pendingFulfillmentStatus, setPendingFulfillmentStatus] = useState<OrderFulfillmentStatus | ''>('');
  const [statusComment, setStatusComment] = useState('');
  const [updateError, setUpdateError] = useState<string | null>(null);

  // Theme-aware colors
  const colors = mode === 'dark' ? {
    surface: palette.surface,
    surfaceElevated: palette.surfaceElevated,
    border: palette.border,
    textPrimary: palette.textPrimary,
    textSecondary: palette.textSecondary,
    textMuted: palette.textMuted,
  } : {
    surface: palette.light.surface,
    surfaceElevated: palette.light.surfaceElevated,
    border: palette.light.border,
    textPrimary: palette.light.textPrimary,
    textSecondary: palette.light.textSecondary,
    textMuted: palette.light.textMuted,
  };

  // Fetch order with all details
  const { data: order, isLoading, error } = useOrder(id, {
    includeItems: true,
    includeCustomer: true,
    includeHistory: true,
    includeReseller: true,
    includeLocation: true,
  });

  // Mutations
  const updatePaymentStatusMutation = useUpdatePaymentStatus();
  const updateFulfillmentStatusMutation = useUpdateFulfillmentStatus();
  const cancelOrderMutation = useCancelOrder();

  // Stock validation state
  const [isConfirmingOrder, setIsConfirmingOrder] = useState(false);
  const queryClient = useQueryClient();

  // Fetch stock check when order is PENDING
  const { data: stockCheck, isLoading: isLoadingStockCheck } = useQuery<OrderStockCheck>({
    queryKey: ['orderStockCheck', id],
    queryFn: async () => {
      const response = await axiosInstance.get(`/orders/${id}/stock-check`);
      return response.data;
    },
    enabled: !!id && order?.fulfillmentStatus === OrderFulfillmentStatus.PENDING,
  });

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('fr-DZ', {
      style: 'currency',
      currency: 'DZD',
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('fr-DZ', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getPaymentMethodLabel = (method: string) => {
    const labels: Record<string, string> = {
      cash: 'Espèces',
      card: 'Carte',
      mobile: 'Paiement Mobile',
      transfer: 'Virement',
      check: 'Chèque',
    };
    return labels[method?.toLowerCase()] || method;
  };

  const getPaymentMethodColor = (method: string) => {
    const colorMap: Record<string, string> = {
      cash: palette.success,
      card: palette.info,
      mobile: palette.accent,
      transfer: palette.primary,
      check: palette.warning,
    };
    return colorMap[method?.toLowerCase()] || palette.textMuted;
  };

  // Handler for payment status update
  const handlePaymentStatusUpdate = () => {
    if (!id || !pendingPaymentStatus) return;

    updatePaymentStatusMutation.mutate(
      {
        id,
        payload: {
          status: pendingPaymentStatus,
          notes: statusComment || undefined,
        },
      },
      {
        onSuccess: () => {
          setPaymentDialogOpen(false);
          setPendingPaymentStatus('');
          setStatusComment('');
          setUpdateError(null);
        },
        onError: (err: any) => {
          setUpdateError(err.response?.data?.message || 'Échec de la mise à jour du statut de paiement');
        },
      }
    );
  };

  // Handler for fulfillment status update
  const handleFulfillmentStatusUpdate = () => {
    if (!id || !pendingFulfillmentStatus) return;

    updateFulfillmentStatusMutation.mutate(
      {
        id,
        payload: {
          status: pendingFulfillmentStatus,
          notes: statusComment || undefined,
        },
      },
      {
        onSuccess: () => {
          setFulfillmentDialogOpen(false);
          setPendingFulfillmentStatus('');
          setStatusComment('');
          setUpdateError(null);
        },
        onError: (err: any) => {
          setUpdateError(err.response?.data?.message || 'Échec de la mise à jour du statut de traitement');
        },
      }
    );
  };

  // Handler for order cancellation
  const handleCancelOrder = () => {
    if (!id) return;

    cancelOrderMutation.mutate(
      { id, reason: statusComment || undefined },
      {
        onSuccess: () => {
          setCancelDialogOpen(false);
          setStatusComment('');
          setUpdateError(null);
        },
        onError: (err: any) => {
          setUpdateError(err.response?.data?.message || 'Échec de l\'annulation de la commande');
        },
      }
    );
  };

  // Quick action handlers
  const handleQuickPaymentAction = (status: OrderPaymentStatus) => {
    setPendingPaymentStatus(status);
    setPaymentDialogOpen(true);
  };

  const handleQuickFulfillmentAction = (status: OrderFulfillmentStatus) => {
    setPendingFulfillmentStatus(status);
    setFulfillmentDialogOpen(true);
  };

  // Handle direct confirmation (sufficient stock)
  const handleStockConfirm = async () => {
    if (!id) return;
    setIsConfirmingOrder(true);
    try {
      await updateFulfillmentStatusMutation.mutateAsync({
        id,
        payload: { status: OrderFulfillmentStatus.CONFIRMED },
      });
      queryClient.invalidateQueries({ queryKey: ['orderStockCheck', id] });
    } catch (error) {
      console.error('Failed to confirm order:', error);
    } finally {
      setIsConfirmingOrder(false);
    }
  };

  // Handle confirmation with transfer requests
  const handleStockTransferRequest = async (transferRequests: TransferRequest[]) => {
    if (!id || !order) return;
    setIsConfirmingOrder(true);
    try {
      // Create transfer requests and update order status
      await axiosInstance.post(`/orders/${id}/confirm-with-transfers`, {
        transferRequests,
      });
      // Invalidate queries to refresh data
      queryClient.invalidateQueries({ queryKey: ['order', id] });
      queryClient.invalidateQueries({ queryKey: ['orders'] });
      queryClient.invalidateQueries({ queryKey: ['orderStockCheck', id] });
    } catch (error) {
      console.error('Failed to confirm order with transfers:', error);
      // Fallback: just update status to WAITING_TRANSFER if endpoint doesn't exist
      try {
        await updateFulfillmentStatusMutation.mutateAsync({
          id,
          payload: {
            status: OrderFulfillmentStatus.WAITING_TRANSFER,
            notes: `Transfer requests: ${JSON.stringify(transferRequests)}`,
          },
        });
      } catch (fallbackError) {
        console.error('Fallback also failed:', fallbackError);
      }
    } finally {
      setIsConfirmingOrder(false);
    }
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <Stack alignItems="center" gap={2}>
          <CircularProgress size={48} color="primary" />
          <Typography variant="body2" color="text.secondary">
            Chargement des détails de la commande...
          </Typography>
        </Stack>
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <Alert severity="error">
          {error instanceof Error ? error.message : 'Échec du chargement de la commande'}
        </Alert>
      </Box>
    );
  }

  if (!order) {
    return (
      <Box>
        <Alert severity="warning">Commande non trouvée</Alert>
      </Box>
    );
  }

  const currentPaymentStatus = order.paymentStatus || OrderPaymentStatus.UNPAID;
  const currentFulfillmentStatus = order.fulfillmentStatus || OrderFulfillmentStatus.PENDING;
  const availablePaymentTransitions = allowedPaymentTransitions[currentPaymentStatus] || [];
  const availableFulfillmentTransitions = allowedFulfillmentTransitions[currentFulfillmentStatus] || [];

  const isFulfillmentTerminal = currentFulfillmentStatus === OrderFulfillmentStatus.COMPLETED ||
    currentFulfillmentStatus === OrderFulfillmentStatus.CANCELLED ||
    currentFulfillmentStatus === OrderFulfillmentStatus.EXPIRED;

  const isB2BOrder = order.orderType === OrderType.B2B_DIRECT || order.orderType === OrderType.B2B_QUOTE;

  return (
    <Box sx={animations.fadeIn}>
      {/* Header */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
        sx={animations.slideDown}
      >
        <Box>
          <Typography
            variant="h4"
            fontWeight={700}
            sx={{
              fontFamily: typography.fontFamily.display,
              background: customTheme.gradients.primary,
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              mb: 0.5,
            }}
          >
            Détails de la Commande
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="body2" color="text.secondary">
              {order.trackingNumber || `#${id?.slice(0, 8)}`}
            </Typography>
            <OrderTypeBadge orderType={order.orderType} size="small" />
          </Stack>
        </Box>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/app/orders')}
          sx={{ borderColor: colors.border, color: colors.textSecondary, ...animations.buttonPress }}
        >
          Retour
        </Button>
      </Box>

      {updateError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setUpdateError(null)}>
          {updateError}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Main Content */}
        <Grid item xs={12} lg={8}>
          <Stack spacing={3}>
            {/* Status Cards */}
            <Box display="grid" gridTemplateColumns={{ xs: '1fr', md: '1fr 1fr' }} gap={3}>
              {/* Payment Status Card */}
              <Stack spacing={2}>
                <PaymentSummaryCard
                  paymentStatus={currentPaymentStatus}
                  totalAmount={order.totalAmount}
                  paidAmount={order.paidAmount || 0}
                  refundedAmount={order.refundedAmount || 0}
                  animationDelay={0.1}
                />

                {/* Payment Quick Actions */}
                {availablePaymentTransitions.length > 0 && (
                  <Card sx={{ bgcolor: colors.surface, border: `1px solid ${colors.border}`, ...animations.slideUp(0.15) }}>
                    <CardContent sx={{ py: 2 }}>
                      <Typography variant="body2" color="text.secondary" mb={1.5}>
                        Actions rapides:
                      </Typography>
                      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                        {currentPaymentStatus === OrderPaymentStatus.UNPAID && (
                          <Button
                            variant="contained"
                            size="small"
                            startIcon={<PaymentIcon />}
                            onClick={() => handleQuickPaymentAction(OrderPaymentStatus.PAID)}
                            disabled={updatePaymentStatusMutation.isPending}
                            sx={{
                              ...animations.buttonPress,
                              bgcolor: palette.success,
                              '&:hover': { bgcolor: palette.successDark },
                            }}
                          >
                            Marquer Payé
                          </Button>
                        )}
                        {currentPaymentStatus === OrderPaymentStatus.PARTIALLY_PAID && (
                          <Button
                            variant="contained"
                            size="small"
                            startIcon={<PaymentIcon />}
                            onClick={() => handleQuickPaymentAction(OrderPaymentStatus.PAID)}
                            disabled={updatePaymentStatusMutation.isPending}
                            sx={{
                              ...animations.buttonPress,
                              bgcolor: palette.success,
                              '&:hover': { bgcolor: palette.successDark },
                            }}
                          >
                            Compléter Paiement
                          </Button>
                        )}
                        {currentPaymentStatus === OrderPaymentStatus.PAID && (
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={<UndoIcon />}
                            onClick={() => setRefundDialogOpen(true)}
                            disabled={updatePaymentStatusMutation.isPending}
                            sx={{
                              ...animations.buttonPress,
                              borderColor: palette.warning,
                              color: palette.warning,
                              '&:hover': {
                                borderColor: palette.warningDark,
                                bgcolor: `${palette.warning}10`,
                              },
                            }}
                          >
                            Initier un remboursement
                          </Button>
                        )}
                      </Stack>

                      <Box display="flex" alignItems="center" gap={2} mt={2}>
                        <FormControl size="small" sx={{ minWidth: 180 }}>
                          <InputLabel>Changer le statut</InputLabel>
                          <Select
                            value=""
                            label="Changer le statut"
                            onChange={(e) => {
                              setPendingPaymentStatus(e.target.value as OrderPaymentStatus);
                              setPaymentDialogOpen(true);
                            }}
                            disabled={updatePaymentStatusMutation.isPending}
                          >
                            {availablePaymentTransitions.map((status) => (
                              <MenuItem key={status} value={status}>
                                {paymentStatusLabels[status] || status}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Box>
                    </CardContent>
                  </Card>
                )}
              </Stack>

              {/* Fulfillment Status Card */}
              <Card sx={{ bgcolor: colors.surface, border: `1px solid ${colors.border}`, ...animations.slideUp(0.15) }}>
                <CardContent>
                  <Typography variant="h6" fontWeight={600} gutterBottom>
                    Statut du Traitement
                  </Typography>
                  <Box display="flex" alignItems="center" gap={2} mb={2}>
                    <FulfillmentStatusChip status={currentFulfillmentStatus} size="medium" />
                  </Box>

                  {/* Status alerts */}
                  {currentFulfillmentStatus === OrderFulfillmentStatus.WAITING_TRANSFER && (
                    <Alert severity="info" icon={<HourglassEmptyIcon />} sx={{ mb: 2 }}>
                      <Typography fontWeight={600}>Attente des transferts de stock</Typography>
                      <Typography variant="body2">
                        Le statut se mettra à jour automatiquement à la fin des transferts.
                      </Typography>
                    </Alert>
                  )}

                  {currentFulfillmentStatus === OrderFulfillmentStatus.TRANSFER_ISSUE && (
                    <Alert severity="error" icon={<WarningAmberIcon />} sx={{ mb: 2 }}>
                      <Typography fontWeight={600}>Problème de transfert détecté</Typography>
                      <Typography variant="body2">
                        Veuillez vérifier le statut des transferts.
                      </Typography>
                    </Alert>
                  )}

                  {currentFulfillmentStatus === OrderFulfillmentStatus.WAITING_ADMIN_ACTION && (
                    <Alert severity="warning" icon={<VerifiedIcon />} sx={{ mb: 2 }}>
                      <Typography fontWeight={600}>Confirmation admin requise</Typography>
                      <Typography variant="body2">
                        Veuillez confirmer la réception des articles.
                      </Typography>
                    </Alert>
                  )}

                  {/* Stock Validation Panel for PENDING orders */}
                  {currentFulfillmentStatus === OrderFulfillmentStatus.PENDING && (
                    <>
                      <Divider sx={{ my: 2, borderColor: colors.border }} />
                      {isLoadingStockCheck ? (
                        <Box display="flex" alignItems="center" gap={1} py={2}>
                          <CircularProgress size={20} />
                          <Typography variant="body2" color="text.secondary">
                            Vérification du stock...
                          </Typography>
                        </Box>
                      ) : stockCheck ? (
                        <OrderStockValidationPanel
                          stockCheck={stockCheck}
                          onConfirm={handleStockConfirm}
                          onRequestTransfer={handleStockTransferRequest}
                          isConfirming={isConfirmingOrder}
                        />
                      ) : (
                        <Alert severity="warning" sx={{ mb: 2 }}>
                          Impossible de vérifier le stock. Vous pouvez toujours confirmer manuellement.
                        </Alert>
                      )}
                    </>
                  )}

                  {!isFulfillmentTerminal && availableFulfillmentTransitions.length > 0 && (
                    <>
                      {currentFulfillmentStatus !== OrderFulfillmentStatus.PENDING && (
                        <>
                          <Divider sx={{ my: 2, borderColor: colors.border }} />
                          <Typography variant="body2" color="text.secondary" mb={1.5}>
                            Actions rapides:
                          </Typography>
                        </>
                      )}
                      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                        {/* PENDING confirm is now handled by OrderStockValidationPanel above */}
                        {currentFulfillmentStatus === OrderFulfillmentStatus.WAITING_ADMIN_ACTION && (
                          <Button
                            variant="contained"
                            size="small"
                            startIcon={<VerifiedIcon />}
                            onClick={() => handleQuickFulfillmentAction(OrderFulfillmentStatus.CONFIRMED)}
                            disabled={updateFulfillmentStatusMutation.isPending}
                            sx={{
                              ...animations.buttonPress,
                              bgcolor: palette.success,
                              '&:hover': { bgcolor: palette.successDark },
                            }}
                          >
                            Confirmer Stock Reçu
                          </Button>
                        )}
                        {currentFulfillmentStatus === OrderFulfillmentStatus.CONFIRMED && (
                          <>
                            {order.fulfillmentMethod === FulfillmentMethod.PICKUP && (
                              <Button
                                variant="contained"
                                size="small"
                                startIcon={<StorefrontIcon />}
                                onClick={() => handleQuickFulfillmentAction(OrderFulfillmentStatus.READY_FOR_PICKUP)}
                                disabled={updateFulfillmentStatusMutation.isPending}
                                sx={{
                                  ...animations.buttonPress,
                                  bgcolor: palette.primary,
                                  '&:hover': { bgcolor: palette.primaryDark },
                                }}
                              >
                                Prêt à Retirer
                              </Button>
                            )}
                            {order.fulfillmentMethod === FulfillmentMethod.DELIVERY && (
                              <Button
                                variant="contained"
                                size="small"
                                startIcon={<LocalShippingOutlinedIcon />}
                                onClick={() => handleQuickFulfillmentAction(OrderFulfillmentStatus.READY_FOR_DELIVERY)}
                                disabled={updateFulfillmentStatusMutation.isPending}
                                sx={{
                                  ...animations.buttonPress,
                                  bgcolor: palette.primary,
                                  '&:hover': { bgcolor: palette.primaryDark },
                                }}
                              >
                                Prêt à Livrer
                              </Button>
                            )}
                          </>
                        )}
                        {currentFulfillmentStatus === OrderFulfillmentStatus.READY_FOR_PICKUP && (
                          <Button
                            variant="contained"
                            size="small"
                            startIcon={<InventoryOutlinedIcon />}
                            onClick={() => handleQuickFulfillmentAction(OrderFulfillmentStatus.COMPLETED)}
                            disabled={updateFulfillmentStatusMutation.isPending}
                            sx={{
                              ...animations.buttonPress,
                              bgcolor: palette.success,
                              '&:hover': { bgcolor: palette.successDark },
                            }}
                          >
                            Terminer Retrait
                          </Button>
                        )}
                        {currentFulfillmentStatus === OrderFulfillmentStatus.READY_FOR_DELIVERY && (
                          <Button
                            variant="contained"
                            size="small"
                            startIcon={<LocalShippingOutlinedIcon />}
                            onClick={() => handleQuickFulfillmentAction(OrderFulfillmentStatus.OUT_FOR_DELIVERY)}
                            disabled={updateFulfillmentStatusMutation.isPending}
                            sx={{
                              ...animations.buttonPress,
                              bgcolor: palette.info,
                              '&:hover': { bgcolor: palette.infoDark },
                            }}
                          >
                            En Livraison
                          </Button>
                        )}
                        {currentFulfillmentStatus === OrderFulfillmentStatus.OUT_FOR_DELIVERY && (
                          <Button
                            variant="contained"
                            size="small"
                            startIcon={<CheckCircleOutlineIcon />}
                            onClick={() => handleQuickFulfillmentAction(OrderFulfillmentStatus.COMPLETED)}
                            disabled={updateFulfillmentStatusMutation.isPending}
                            sx={{
                              ...animations.buttonPress,
                              bgcolor: palette.success,
                              '&:hover': { bgcolor: palette.successDark },
                            }}
                          >
                            Marquer Livré
                          </Button>
                        )}
                        {availableFulfillmentTransitions.includes(OrderFulfillmentStatus.CANCELLED) && (
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={<CancelOutlinedIcon />}
                            onClick={() => setCancelDialogOpen(true)}
                            disabled={cancelOrderMutation.isPending}
                            sx={{
                              ...animations.buttonPress,
                              borderColor: palette.error,
                              color: palette.error,
                              '&:hover': {
                                borderColor: palette.errorDark,
                                bgcolor: palette.errorMuted,
                              },
                            }}
                          >
                            Annuler
                          </Button>
                        )}
                      </Stack>

                      <Box display="flex" alignItems="center" gap={2} mt={2}>
                        <FormControl size="small" sx={{ minWidth: 180 }}>
                          <InputLabel>Changer le statut</InputLabel>
                          <Select
                            value=""
                            label="Changer le statut"
                            onChange={(e) => {
                              const status = e.target.value as OrderFulfillmentStatus;
                              if (status === OrderFulfillmentStatus.CANCELLED) {
                                setCancelDialogOpen(true);
                              } else {
                                setPendingFulfillmentStatus(status);
                                setFulfillmentDialogOpen(true);
                              }
                            }}
                            disabled={updateFulfillmentStatusMutation.isPending}
                          >
                            {availableFulfillmentTransitions.map((status) => (
                              <MenuItem key={status} value={status}>
                                {fulfillmentStatusLabels[status] || status}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Box>
                    </>
                  )}

                  {isFulfillmentTerminal && (
                    <Alert
                      severity={currentFulfillmentStatus === OrderFulfillmentStatus.COMPLETED ? 'success' : 'info'}
                      sx={{ mt: 2 }}
                    >
                      Cette commande est {fulfillmentStatusLabels[currentFulfillmentStatus]} et ne peut pas être modifiée.
                    </Alert>
                  )}
                </CardContent>
              </Card>
            </Box>

            {/* Planned Transfers Card */}
            {order.plannedTransfers && order.plannedTransfers.length > 0 && (
              <Card sx={{ bgcolor: colors.surface, border: `1px solid ${colors.border}`, ...animations.slideUp(0.2) }}>
                <CardContent>
                  <Box display="flex" alignItems="center" gap={1} mb={2}>
                    <SwapHorizIcon sx={{ color: palette.info }} />
                    <Typography variant="h6" fontWeight={600}>
                      Transferts de Stock Prévus ({order.plannedTransfers.length})
                    </Typography>
                  </Box>

                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ color: colors.textSecondary, fontWeight: 600 }}>Produit</TableCell>
                        <TableCell sx={{ color: colors.textSecondary, fontWeight: 600 }}>De</TableCell>
                        <TableCell sx={{ color: colors.textSecondary, fontWeight: 600 }}>Vers</TableCell>
                        <TableCell align="right" sx={{ color: colors.textSecondary, fontWeight: 600 }}>Qté</TableCell>
                        <TableCell sx={{ color: colors.textSecondary, fontWeight: 600 }}>Statut</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {order.plannedTransfers.map((transfer: any, index: number) => (
                        <TableRow key={index}>
                          <TableCell>{transfer.productName}</TableCell>
                          <TableCell>
                            <Box display="flex" alignItems="center" gap={0.5}>
                              <LocationOnIcon sx={{ fontSize: 16, color: colors.textMuted }} />
                              {transfer.fromLocationName}
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Box display="flex" alignItems="center" gap={0.5}>
                              <LocationOnIcon sx={{ fontSize: 16, color: palette.primary }} />
                              {transfer.toLocationName}
                            </Box>
                          </TableCell>
                          <TableCell align="right">{transfer.quantity}</TableCell>
                          <TableCell>
                            {currentFulfillmentStatus === OrderFulfillmentStatus.WAITING_TRANSFER ? (
                              <Chip
                                label="En cours"
                                size="small"
                                sx={{ bgcolor: palette.infoMuted, color: palette.info, fontSize: '0.75rem' }}
                              />
                            ) : currentFulfillmentStatus === OrderFulfillmentStatus.TRANSFER_ISSUE ? (
                              <Chip
                                label="Problème"
                                size="small"
                                sx={{ bgcolor: palette.errorMuted, color: palette.error, fontSize: '0.75rem' }}
                              />
                            ) : (
                              <Chip
                                label="Terminé"
                                size="small"
                                sx={{ bgcolor: palette.successMuted, color: palette.success, fontSize: '0.75rem' }}
                              />
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            )}

            {/* Delivery Address Card */}
            {order.deliveryAddress && (
              <Card sx={{ bgcolor: colors.surface, border: `1px solid ${colors.border}`, ...animations.slideUp(0.25) }}>
                <CardContent>
                  <Box display="flex" alignItems="center" gap={1} mb={2}>
                    <LocalShippingOutlinedIcon sx={{ color: palette.primary }} />
                    <Typography variant="h6" fontWeight={600}>
                      Adresse de Livraison
                    </Typography>
                  </Box>

                  <Box display="grid" gridTemplateColumns={{ xs: '1fr', sm: '1fr 1fr' }} gap={2}>
                    <Box>
                      <Typography variant="body2" color="text.secondary">Rue</Typography>
                      <Typography variant="body1">{order.deliveryAddress.street}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="body2" color="text.secondary">Ville</Typography>
                      <Typography variant="body1">{order.deliveryAddress.city}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="body2" color="text.secondary">Wilaya</Typography>
                      <Typography variant="body1">{order.deliveryAddress.state}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="body2" color="text.secondary">Code Postal</Typography>
                      <Typography variant="body1">{order.deliveryAddress.postalCode}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="body2" color="text.secondary">Pays</Typography>
                      <Typography variant="body1">{order.deliveryAddress.country}</Typography>
                    </Box>
                    {order.deliveryAddress.notes && (
                      <Box gridColumn="1 / -1">
                        <Typography variant="body2" color="text.secondary">Notes de livraison</Typography>
                        <Typography variant="body1">{order.deliveryAddress.notes}</Typography>
                      </Box>
                    )}
                  </Box>
                </CardContent>
              </Card>
            )}

            {/* Order Items Card */}
            {order.items && order.items.length > 0 && (
              <Card sx={{ bgcolor: colors.surface, border: `1px solid ${colors.border}`, ...animations.slideUp(0.3) }}>
                <CardContent>
                  <Typography variant="h6" fontWeight={600} gutterBottom>
                    Articles ({order.items.length})
                  </Typography>
                  <Box
                    sx={{
                      border: (theme) => `1px solid ${theme.palette.divider}`,
                      borderRadius: customTheme.radius.lg,
                      overflow: 'hidden',
                    }}
                  >
                    <Table>
                      <TableHead>
                        <TableRow sx={{ bgcolor: 'action.hover' }}>
                          <TableCell sx={{ fontWeight: 700, fontSize: '12px', textTransform: 'uppercase', letterSpacing: typography.letterSpacing.wider }}>
                            Produit
                          </TableCell>
                          <TableCell align="center" sx={{ fontWeight: 700, fontSize: '12px', textTransform: 'uppercase', letterSpacing: typography.letterSpacing.wider }}>
                            Qté
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700, fontSize: '12px', textTransform: 'uppercase', letterSpacing: typography.letterSpacing.wider }}>
                            Prix
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700, fontSize: '12px', textTransform: 'uppercase', letterSpacing: typography.letterSpacing.wider }}>
                            Sous-total
                          </TableCell>
                          {canViewCosts && (
                            <>
                              <TableCell align="right" sx={{ fontWeight: 700, fontSize: '12px', textTransform: 'uppercase', letterSpacing: typography.letterSpacing.wider }}>
                                Coût
                              </TableCell>
                              <TableCell align="right" sx={{ fontWeight: 700, fontSize: '12px', textTransform: 'uppercase', letterSpacing: typography.letterSpacing.wider }}>
                                Marge
                              </TableCell>
                            </>
                          )}
                          {(order.refundedAmount > 0 || order.items?.some((i: any) => i.refundedQuantity > 0)) && (
                            <TableCell align="center" sx={{ fontWeight: 700, fontSize: '12px', textTransform: 'uppercase', letterSpacing: typography.letterSpacing.wider }}>
                              Remboursement
                            </TableCell>
                          )}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {order.items.map((item: any, index: number) => (
                          <TableRow
                            key={item.id}
                            sx={{
                              ...animations.tableRowFade(index),
                              ...(item.refundedQuantity >= item.quantity && {
                                opacity: 0.6,
                                bgcolor: `${palette.error}05`,
                              }),
                            }}
                          >
                            <TableCell>
                              <Typography
                                variant="body2"
                                fontWeight={600}
                                sx={{
                                  ...(item.refundedQuantity >= item.quantity && {
                                    textDecoration: 'line-through',
                                    color: 'text.secondary',
                                  }),
                                }}
                              >
                                {item.productName || item.product?.name || 'Produit inconnu'}
                              </Typography>
                              {item.variantName && (
                                <Typography variant="caption" color="text.secondary">
                                  {item.variantName}
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell align="center">
                              <Chip
                                label={item.quantity}
                                size="small"
                                color="primary"
                                variant="outlined"
                                sx={{ fontWeight: 700, minWidth: 36 }}
                              />
                            </TableCell>
                            <TableCell align="right">
                              <Typography variant="body2" sx={{ fontFamily: typography.fontFamily.mono, color: colors.textSecondary }}>
                                {formatCurrency(item.unitPrice || item.price)}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              <Typography variant="body2" fontWeight={600} sx={{ fontFamily: typography.fontFamily.mono }}>
                                {formatCurrency(item.subtotal || (item.unitPrice || item.price) * item.quantity)}
                              </Typography>
                            </TableCell>
                            {canViewCosts && (
                              <>
                                <TableCell align="right">
                                  <Typography variant="body2" sx={{ fontFamily: typography.fontFamily.mono, color: colors.textSecondary }}>
                                    {item.totalCost != null ? formatCurrency(item.totalCost) : '-'}
                                  </Typography>
                                </TableCell>
                                <TableCell align="right">
                                  <Typography
                                    variant="body2"
                                    fontWeight={600}
                                    sx={{
                                      fontFamily: typography.fontFamily.mono,
                                      color: item.margin != null
                                        ? (item.margin >= 0 ? palette.success : palette.error)
                                        : colors.textSecondary,
                                    }}
                                  >
                                    {item.margin != null ? `${item.margin > 0 ? '+' : ''}${formatCurrency(item.margin)}` : '-'}
                                  </Typography>
                                </TableCell>
                              </>
                            )}
                            {(order.refundedAmount > 0 || order.items?.some((i: any) => i.refundedQuantity > 0)) && (
                              <TableCell align="center">
                                {item.refundedQuantity > 0 ? (
                                  <Chip
                                    label={
                                      item.refundedQuantity >= item.quantity
                                        ? 'Remboursé'
                                        : `${item.refundedQuantity}/${item.quantity}`
                                    }
                                    size="small"
                                    sx={{
                                      fontWeight: 600,
                                      fontSize: '11px',
                                      bgcolor: item.refundedQuantity >= item.quantity
                                        ? `${palette.error}15`
                                        : `${palette.warning}15`,
                                      color: item.refundedQuantity >= item.quantity
                                        ? palette.error
                                        : palette.warning,
                                      border: `1px solid ${item.refundedQuantity >= item.quantity ? palette.error : palette.warning}30`,
                                    }}
                                  />
                                ) : (
                                  <Typography variant="body2" color="text.secondary">-</Typography>
                                )}
                              </TableCell>
                            )}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </Box>

                  {/* Totals */}
                  <Box sx={{ borderTop: (theme) => `2px dashed ${theme.palette.divider}`, pt: 3, mt: 3 }}>
                    <Stack spacing={2}>
                      <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Typography variant="body2" color="text.secondary">Sous-total</Typography>
                        <Typography variant="body1" fontWeight={600} sx={{ fontFamily: typography.fontFamily.mono }}>
                          {formatCurrency(order.subtotal || 0)}
                        </Typography>
                      </Box>
                      {order.tax > 0 && (
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                          <Typography variant="body2" color="text.secondary">TVA (19%)</Typography>
                          <Typography variant="body1" fontWeight={600} sx={{ fontFamily: typography.fontFamily.mono }}>
                            {formatCurrency(order.tax)}
                          </Typography>
                        </Box>
                      )}
                      {order.discount > 0 && (
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                          <Typography variant="body2" color="text.secondary">Remise</Typography>
                          <Typography variant="body1" fontWeight={600} color="success.main" sx={{ fontFamily: typography.fontFamily.mono }}>
                            -{formatCurrency(order.discount)}
                          </Typography>
                        </Box>
                      )}
                      {order.refundedAmount > 0 && (
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                          <Typography variant="body2" color="text.secondary">Remboursé</Typography>
                          <Typography variant="body1" fontWeight={600} color="error.main" sx={{ fontFamily: typography.fontFamily.mono }}>
                            -{formatCurrency(order.refundedAmount)}
                          </Typography>
                        </Box>
                      )}
                      <Divider sx={{ my: 1 }} />
                      <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                        sx={{
                          bgcolor: 'action.hover',
                          p: 2,
                          borderRadius: customTheme.radius.lg,
                        }}
                      >
                        <Typography variant="h6" fontWeight={700} sx={{ fontFamily: typography.fontFamily.display }}>
                          {order.refundedAmount > 0 ? 'Total net' : 'Total'}
                        </Typography>
                        <Typography
                          variant="h5"
                          fontWeight={800}
                          sx={{
                            fontFamily: typography.fontFamily.mono,
                            background: customTheme.gradients.primary,
                            backgroundClip: 'text',
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                          }}
                        >
                          {formatCurrency((order.totalAmount || 0) - (order.refundedAmount || 0))}
                        </Typography>
                      </Box>
                    </Stack>
                  </Box>
                </CardContent>
              </Card>
            )}
          </Stack>
        </Grid>

        {/* Sidebar */}
        <Grid item xs={12} lg={4}>
          <Stack spacing={3}>
            {/* Order Info Card */}
            <Card sx={{ bgcolor: colors.surface, border: `1px solid ${colors.border}`, ...animations.slideRight(0.2) }}>
              <CardContent sx={{ p: 3 }}>
                <Box display="flex" alignItems="center" gap={2} mb={3} pb={3} sx={{ borderBottom: (theme) => `1px dashed ${theme.palette.divider}` }}>
                  <Box
                    sx={{
                      width: 56,
                      height: 56,
                      borderRadius: customTheme.radius.lg,
                      background: customTheme.gradients.primary,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <ReceiptLongIcon sx={{ fontSize: 28, color: '#FFFFFF' }} />
                  </Box>
                  <Box flex={1}>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ textTransform: 'uppercase', letterSpacing: typography.letterSpacing.wider, fontSize: '11px', fontWeight: 600 }}
                    >
                      Numéro de suivi
                    </Typography>
                    <Typography variant="h6" fontWeight={700} color="text.primary" sx={{ fontFamily: typography.fontFamily.mono }}>
                      {order.trackingNumber || 'N/A'}
                    </Typography>
                  </Box>
                </Box>

                <Stack spacing={3}>
                  {/* Customer */}
                  <Box>
                    <Box display="flex" alignItems="center" gap={1} mb={1}>
                      <PersonOutlineIcon sx={{ fontSize: 18 }} color="action" />
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ textTransform: 'uppercase', letterSpacing: typography.letterSpacing.wider, fontWeight: 600 }}
                      >
                        Client
                      </Typography>
                    </Box>
                    <Typography variant="body1" fontWeight={600}>
                      {order.customer
                        ? `${order.customer.firstName} ${order.customer.lastName}`
                        : order.customerName || 'Client anonyme'}
                    </Typography>
                    {(order.customer?.email || order.customerEmail) && (
                      <Typography variant="body2" color="text.secondary">
                        {order.customer?.email || order.customerEmail}
                      </Typography>
                    )}
                    {(order.customer?.phone || order.customerPhone) && (
                      <Typography variant="body2" color="text.secondary">
                        {order.customer?.phone || order.customerPhone}
                      </Typography>
                    )}
                  </Box>

                  <Divider />

                  {/* Payment Method */}
                  <Box>
                    <Box display="flex" alignItems="center" gap={1} mb={1}>
                      <PaymentIcon sx={{ fontSize: 18 }} color="action" />
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ textTransform: 'uppercase', letterSpacing: typography.letterSpacing.wider, fontWeight: 600 }}
                      >
                        Méthode de paiement
                      </Typography>
                    </Box>
                    <Chip
                      label={getPaymentMethodLabel(order.paymentMethod || "")}
                      sx={{
                        bgcolor: `${getPaymentMethodColor(order.paymentMethod || "")}20`,
                        color: getPaymentMethodColor(order.paymentMethod || ""),
                        fontWeight: 600,
                        fontSize: '13px',
                      }}
                    />
                  </Box>

                  <Divider />

                  {/* Fulfillment Method */}
                  <Box>
                    <Box display="flex" alignItems="center" gap={1} mb={1}>
                      <LocalShippingOutlinedIcon sx={{ fontSize: 18 }} color="action" />
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ textTransform: 'uppercase', letterSpacing: typography.letterSpacing.wider, fontWeight: 600 }}
                      >
                        Mode de livraison
                      </Typography>
                    </Box>
                    <Chip
                      label={order.fulfillmentMethod === FulfillmentMethod.DELIVERY ? 'Livraison' : 'Retrait en magasin'}
                      sx={{
                        bgcolor: `${palette.info}20`,
                        color: palette.info,
                        fontWeight: 600,
                        fontSize: '13px',
                      }}
                    />
                  </Box>

                  <Divider />

                  {/* Date */}
                  <Box>
                    <Box display="flex" alignItems="center" gap={1} mb={1}>
                      <CalendarTodayIcon sx={{ fontSize: 18 }} color="action" />
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ textTransform: 'uppercase', letterSpacing: typography.letterSpacing.wider, fontWeight: 600 }}
                      >
                        Date de commande
                      </Typography>
                    </Box>
                    <Typography variant="body2" fontWeight={500}>
                      {order.createdAt ? formatDate(order.createdAt) : 'N/A'}
                    </Typography>
                  </Box>

                  {order.completedAt && (
                    <>
                      <Divider />
                      <Box>
                        <Box display="flex" alignItems="center" gap={1} mb={1}>
                          <CheckCircleOutlineIcon sx={{ fontSize: 18, color: palette.success }} />
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ textTransform: 'uppercase', letterSpacing: typography.letterSpacing.wider, fontWeight: 600 }}
                          >
                            Date de fin
                          </Typography>
                        </Box>
                        <Typography variant="body2" fontWeight={500} color={palette.success}>
                          {formatDate(order.completedAt)}
                        </Typography>
                      </Box>
                    </>
                  )}

                  {order.location && (
                    <>
                      <Divider />
                      <Box>
                        <Box display="flex" alignItems="center" gap={1} mb={1}>
                          <StorefrontIcon sx={{ fontSize: 18 }} color="action" />
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ textTransform: 'uppercase', letterSpacing: typography.letterSpacing.wider, fontWeight: 600 }}
                          >
                            Magasin
                          </Typography>
                        </Box>
                        <Typography variant="body1" fontWeight={600}>
                          {order.location.name}
                        </Typography>
                      </Box>
                    </>
                  )}
                </Stack>
              </CardContent>
            </Card>

            {/* B2B Info Card */}
            {isB2BOrder && (
              <Card
                sx={{
                  bgcolor: (theme) => theme.palette.mode === 'light' ? `${palette.accent}08` : `${palette.accent}10`,
                  border: `1px solid ${palette.accent}30`,
                  ...animations.slideRight(0.25),
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Box display="flex" alignItems="center" gap={1.5} mb={2}>
                    <Box
                      sx={{
                        width: 32,
                        height: 32,
                        borderRadius: customTheme.radius.md,
                        bgcolor: `${palette.accent}20`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <BusinessIcon sx={{ fontSize: 18, color: palette.accent }} />
                    </Box>
                    <Typography variant="h6" fontWeight={700} sx={{ fontFamily: typography.fontFamily.display }}>
                      Détails B2B
                    </Typography>
                  </Box>

                  <Stack spacing={2}>
                    <>
                    {order.reseller && (
                      <Box>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ textTransform: 'uppercase', letterSpacing: typography.letterSpacing.wider, fontWeight: 600 }}
                        >
                          Revendeur
                        </Typography>
                        <Typography variant="body2" fontWeight={600} mt={0.5}>
                          {order.reseller.companyName || `${order.reseller.user?.firstName} ${order.reseller.user?.lastName}`}
                        </Typography>
                      </Box>
                    )}
                    {order.pricingTier && (
                      <Box>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ textTransform: 'uppercase', letterSpacing: typography.letterSpacing.wider, fontWeight: 600 }}
                        >
                          Niveau de prix
                        </Typography>
                        <Box mt={0.5}>
                          <Chip
                            label={order.pricingTier.tierName}
                            size="small"
                            sx={{ bgcolor: `${palette.info}20`, color: palette.info, fontWeight: 600, textTransform: 'capitalize' }}
                          />
                        </Box>
                      </Box>
                    )}
                    {order.b2bMetadata?.poNumber && (
                      <Box>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ textTransform: 'uppercase', letterSpacing: typography.letterSpacing.wider, fontWeight: 600 }}
                        >
                          Numéro PO
                        </Typography>
                        <Typography variant="body2" fontWeight={600} mt={0.5}>
                          {String(order.b2bMetadata.poNumber)}
                        </Typography>
                      </Box>
                    )}
                    {order.b2bMetadata?.paymentTerms && (
                      <Box>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ textTransform: 'uppercase', letterSpacing: typography.letterSpacing.wider, fontWeight: 600 }}
                        >
                          Conditions de paiement
                        </Typography>
                        <Typography variant="body2" fontWeight={600} mt={0.5}>
                          {String(order.b2bMetadata.paymentTerms).replace(/_/g, ' ').toUpperCase()}
                        </Typography>
                      </Box>
                    )}
                    </>
                  </Stack>
                </CardContent>
              </Card>
            )}

            {/* Notes Card */}
            {order.notes && (
              <Card
                sx={{
                  bgcolor: (theme) => theme.palette.mode === 'light' ? `${palette.accent}08` : `${palette.accent}10`,
                  border: (theme) => `1px solid ${theme.palette.mode === 'light' ? `${palette.accent}20` : `${palette.accent}30`}`,
                  ...animations.slideRight(0.3),
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Typography
                    variant="subtitle2"
                    fontWeight={700}
                    mb={1.5}
                    sx={{ color: palette.accent, textTransform: 'uppercase', letterSpacing: typography.letterSpacing.wider, fontSize: '11px' }}
                  >
                    Notes
                  </Typography>
                  <Typography variant="body2" color="text.primary" sx={{ lineHeight: 1.6 }}>
                    {order.notes}
                  </Typography>
                </CardContent>
              </Card>
            )}

            {/* History Timeline Card */}
            <Card sx={{ bgcolor: colors.surface, border: `1px solid ${colors.border}`, ...animations.slideRight(0.35) }}>
              <CardContent>
                <Box display="flex" alignItems="center" gap={1} mb={2}>
                  <HistoryIcon sx={{ color: palette.primary }} />
                  <Typography variant="h6" fontWeight={600}>
                    Historique
                  </Typography>
                </Box>
                <OrderHistoryTimeline history={order.history} isLoading={false} />
              </CardContent>
            </Card>
          </Stack>
        </Grid>
      </Grid>

      {/* Payment Status Dialog */}
      <Dialog
        open={paymentDialogOpen}
        onClose={() => {
          setPaymentDialogOpen(false);
          setStatusComment('');
          setPendingPaymentStatus('');
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Mettre à jour le statut de paiement
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" mb={2}>
            Vous allez changer le statut de paiement vers <strong>{pendingPaymentStatus ? paymentStatusLabels[pendingPaymentStatus] : ''}</strong>.
          </Typography>
          <TextField
            label="Commentaire (optionnel)"
            multiline
            rows={3}
            fullWidth
            value={statusComment}
            onChange={(e) => setStatusComment(e.target.value)}
            placeholder="Ajoutez une note concernant ce changement..."
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setPaymentDialogOpen(false);
              setStatusComment('');
              setPendingPaymentStatus('');
            }}
            color="inherit"
            sx={animations.buttonPress}
          >
            Annuler
          </Button>
          <Button
            onClick={handlePaymentStatusUpdate}
            variant="contained"
            color="primary"
            disabled={updatePaymentStatusMutation.isPending}
            sx={animations.buttonPress}
          >
            {updatePaymentStatusMutation.isPending ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              'Confirmer'
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Fulfillment Status Dialog */}
      <Dialog
        open={fulfillmentDialogOpen}
        onClose={() => {
          setFulfillmentDialogOpen(false);
          setStatusComment('');
          setPendingFulfillmentStatus('');
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Mettre à jour le statut de traitement
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" mb={2}>
            Vous allez changer le statut de traitement vers <strong>{pendingFulfillmentStatus ? fulfillmentStatusLabels[pendingFulfillmentStatus] : ''}</strong>.
          </Typography>
          <TextField
            label="Commentaire (optionnel)"
            multiline
            rows={3}
            fullWidth
            value={statusComment}
            onChange={(e) => setStatusComment(e.target.value)}
            placeholder="Ajoutez une note concernant ce changement..."
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setFulfillmentDialogOpen(false);
              setStatusComment('');
              setPendingFulfillmentStatus('');
            }}
            color="inherit"
            sx={animations.buttonPress}
          >
            Annuler
          </Button>
          <Button
            onClick={handleFulfillmentStatusUpdate}
            variant="contained"
            color="primary"
            disabled={updateFulfillmentStatusMutation.isPending}
            sx={animations.buttonPress}
          >
            {updateFulfillmentStatusMutation.isPending ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              'Confirmer'
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Cancel Order Dialog */}
      <Dialog
        open={cancelDialogOpen}
        onClose={() => {
          setCancelDialogOpen(false);
          setStatusComment('');
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Annuler la commande
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" mb={2}>
            Êtes-vous sûr de vouloir annuler cette commande ? Cette action ne peut pas être annulée.
          </Typography>
          <TextField
            label="Raison de l'annulation (optionnel)"
            multiline
            rows={3}
            fullWidth
            value={statusComment}
            onChange={(e) => setStatusComment(e.target.value)}
            placeholder="Entrez la raison de l'annulation..."
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setCancelDialogOpen(false);
              setStatusComment('');
            }}
            color="inherit"
            sx={animations.buttonPress}
          >
            Retour
          </Button>
          <Button
            onClick={handleCancelOrder}
            variant="contained"
            color="error"
            disabled={cancelOrderMutation.isPending}
            sx={animations.buttonPress}
          >
            {cancelOrderMutation.isPending ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              'Annuler la commande'
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Refund Modal */}
      {order && (
        <OrderRefundModal
          open={refundDialogOpen}
          order={{
            id: order.id,
            trackingNumber: order.trackingNumber,
            totalAmount: order.totalAmount,
            subtotal: order.subtotal,
            tax: order.tax,
            discount: order.discount,
            paymentMethod: order.paymentMethod || '',
            isRefunded: order.isRefunded || false,
            refundedAmount: order.refundedAmount || 0,
            refundedAt: order.refundedAt || null,
            items: (order.items || []).map((item: any) => ({
              id: item.id,
              productId: item.productId,
              productName: item.productName || item.product?.name || 'Produit inconnu',
              variantName: item.variantName,
              quantity: item.quantity,
              unitPrice: item.unitPrice || item.price,
              subtotal: item.subtotal || (item.unitPrice || item.price) * item.quantity,
            })),
            createdAt: order.createdAt,
          }}
          onClose={() => setRefundDialogOpen(false)}
          onSuccess={() => {
            // Invalidate queries to refresh the order data
            queryClient.invalidateQueries({ queryKey: ['order', id] });
            queryClient.invalidateQueries({ queryKey: ['orders'] });
          }}
        />
      )}
    </Box>
  );
};

export default OrderShow;
